import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Logo,
} from "@exhousing/design-system";
import { CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { useSignup } from "@/hooks/api/useAuth";

const schema = z.object({
  organization_name: z.string().min(2, "Enter your agency name"),
  first_name: z.string().min(1, "Required"),
  last_name: z.string().min(1, "Required"),
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(8, "At least 8 characters"),
});
type FormValues = z.infer<typeof schema>;

export function SignupPage() {
  const signup = useSignup();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit((values) => {
    signup.mutate(values, { onError: (err) => toast.error(err.message) });
  });

  return (
    <div className="relative flex min-h-full items-center justify-center overflow-hidden px-4 py-12">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-sand-100 via-background to-brand-50" />
      <div className="pointer-events-none absolute -right-32 -top-32 -z-10 size-[28rem] rounded-full bg-clay-100/40 blur-3xl" />

      <div className="w-full max-w-md animate-fade-in-up">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo showWordmark={false} className="mb-5 scale-125" />
          <h1 className="font-display text-3xl font-medium tracking-tight">
            Create your agency account
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Start managing your rentals on Balatin
          </p>
        </div>

        <Card className="shadow-lg">
          <CardContent className="pt-6">
            {signup.isSuccess ? (
              <div className="py-6 text-center">
                <CheckCircle2 className="mx-auto size-12 text-success" />
                <h2 className="mt-3 font-display text-xl font-medium">Check your inbox</h2>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  We&apos;ve sent a verification link to activate your account. Once
                  verified, you can sign in.
                </p>
                <Link
                  to="/login"
                  className="mt-5 inline-block text-sm font-medium text-primary hover:underline"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Agency name</Label>
                  <Input id="organization_name" {...register("organization_name")} />
                  {errors.organization_name && (
                    <p className="text-xs text-danger">
                      {errors.organization_name.message}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name</Label>
                    <Input id="first_name" {...register("first_name")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name</Label>
                    <Input id="last_name" {...register("last_name")} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Work email</Label>
                  <Input id="email" type="email" {...register("email")} />
                  {errors.email && (
                    <p className="text-xs text-danger">{errors.email.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" {...register("password")} />
                  {errors.password && (
                    <p className="text-xs text-danger">{errors.password.message}</p>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={signup.isPending}>
                  {signup.isPending ? "Creating account…" : "Create account"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {!signup.isSuccess && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
